/**
 * Hooks for workspace management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceApi } from '../api';
import type { CreateWorkspaceRequest, Workspace } from '../types';

const WORKSPACES_KEY = ['workspaces'];

/**
 * Hook for fetching user's workspaces
 */
export function useWorkspaces(): {
  data: Workspace[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const query = useQuery<Workspace[]>({
    queryKey: WORKSPACES_KEY,
    queryFn: workspaceApi.list,
    refetchInterval: 5000, // Poll every 5 seconds for status updates
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => { void query.refetch(); },
  };
}

/**
 * Hook for fetching a specific workspace
 */
export function useWorkspace(id: string): {
  data: Workspace | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery<Workspace>({
    queryKey: [...WORKSPACES_KEY, id],
    queryFn: () => workspaceApi.get(id),
    refetchInterval: (query) => {
      // Poll when building/starting, slower when running
      const status = query.state.data?.status;
      if (status === 'building') return 5000;
      if (status === 'starting') return 2000;
      if (status === 'running') return 10000;
      return false; // Stop polling when terminated
    },
  });

  return { data, isLoading, error };
}

/**
 * Hook for creating a workspace
 */
export function useCreateWorkspace(): {
  mutateAsync: (data: CreateWorkspaceRequest) => Promise<Workspace>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation<
    Workspace,
    Error,
    CreateWorkspaceRequest
  >({
    mutationFn: workspaceApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
    },
  });

  return { mutateAsync, isPending, error };
}

/**
 * Hook for deleting a workspace
 */
export function useDeleteWorkspace(): {
  mutateAsync: (id: string) => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation<undefined, Error, string>({
    mutationFn: async (id: string) => {
      await workspaceApi.delete(id);
      return undefined;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
    },
  });

  return { mutateAsync, isPending, error };
}

/**
 * Hook for extending a workspace session
 */
export function useExtendWorkspace(): {
  mutateAsync: (id: string) => Promise<Workspace>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation<Workspace, Error, string>({
    mutationFn: workspaceApi.extend,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
    },
  });

  return { mutateAsync, isPending, error };
}
